import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search, DollarSign, TrendingUp, TrendingDown, CheckCircle, FileText, ArrowRight, MapPin, Zap, ShieldAlert } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from '@/components/ui/command';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import AffiliateCta from '@/components/shared/AffiliateCta';
import FinancialStatusInsight from '@/components/shared/FinancialStatusInsight';
import FreeReportModal from '@/components/shared/FreeReportModal';
import { supabase } from '@/integrations/supabase/client';
import { STATE_MAPPINGS } from '@/lib/stateUtils';
import { toTitleCase } from '@/lib/utils';
import { calculateAIRiskScore } from '@/lib/aiRisk';
import { getAiEconomyOccupation, AI_ECONOMY_STATS } from '@/lib/aiEconomyConfig';

interface OccOption {
  soc_code: string;
  occ_title: string;
  slug: string;
}

interface SalaryDistribution {
  occ_title?: string;
  annual_10th_percentile_wage?: number;
  annual_25th_percentile_wage?: number;
  annual_median_wage?: number;
  annual_75th_percentile_wage?: number;
  annual_90th_percentile_wage?: number;
  annual_mean_wage?: number;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(Math.round(value));

// Piecewise-linear estimate of the percentile rank for a salary given the BLS
// percentile breakpoints. Extrapolates gently beyond P10/P90.
const estimatePercentile = (salary: number, dist: SalaryDistribution): number => {
  const points: Array<[number, number]> = [
    [10, dist.annual_10th_percentile_wage ?? 0],
    [25, dist.annual_25th_percentile_wage ?? 0],
    [50, dist.annual_median_wage ?? 0],
    [75, dist.annual_75th_percentile_wage ?? 0],
    [90, dist.annual_90th_percentile_wage ?? 0],
  ].filter(([, wage]) => wage > 0);

  if (points.length < 2) return 50;

  if (salary <= points[0][1]) {
    return Math.max(1, Math.round(points[0][0] * (salary / points[0][1])));
  }
  const last = points[points.length - 1];
  if (salary >= last[1]) {
    return Math.min(99, Math.round(last[0] + (100 - last[0]) * (1 - last[1] / salary)));
  }
  for (let i = 0; i < points.length - 1; i++) {
    const [pLo, wLo] = points[i];
    const [pHi, wHi] = points[i + 1];
    if (salary >= wLo && salary <= wHi) {
      const ratio = (salary - wLo) / (wHi - wLo);
      return Math.round(pLo + ratio * (pHi - pLo));
    }
  }
  return 50;
};

interface UnderpaidCheckerProps {
  /**
   * `full` (default) renders the standalone tool used on the /am-i-underpaid
   * page. `compact` tightens the header for embedding inside the homepage hero.
   */
  variant?: 'full' | 'compact';
  className?: string;
  /** Fires when the user picks an occupation from the autocomplete list. */
  onOccupationSelect?: (occ: { code: string; title: string }) => void;
}

const UnderpaidChecker = ({ variant = 'full', className = '', onOccupationSelect }: UnderpaidCheckerProps) => {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [selected, setSelected] = useState<OccOption | null>(null);
  const [locationSlug, setLocationSlug] = useState('united-states');
  const [salaryInput, setSalaryInput] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), 300);
    return () => clearTimeout(t);
  }, [query]);

  const locationName = locationSlug === 'united-states'
    ? 'United States'
    : STATE_MAPPINGS.find((s) => s.slug === locationSlug)?.name || 'United States';

  const { data: suggestions } = useQuery({
    queryKey: ['underpaid-occ-search', debounced],
    enabled: debounced.length >= 2 && !selected,
    staleTime: 5 * 60 * 1000, // reuse suggestion results within a session
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('api-occupations', {
        body: { search: debounced, limit: 8 },
      });
      if (error) throw error;
      return (data?.occupations || []) as OccOption[];
    },
  });

  const { data: distribution, isLoading: distLoading } = useQuery({
    queryKey: ['underpaid-salary', selected?.soc_code, locationName],
    enabled: submitted && !!selected?.soc_code,
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('api-salary', {
        body: { area: locationName, soc: selected?.soc_code },
      });
      if (error) throw error;
      return data as SalaryDistribution;
    },
  });

  const salaryNumber = Number(salaryInput.replace(/[^0-9.]/g, ''));
  const canSubmit = !!selected && salaryNumber > 0;

  const percentile = distribution && salaryNumber > 0 ? estimatePercentile(salaryNumber, distribution) : null;
  const median = distribution?.annual_median_wage ?? 0;

  // The distribution bar spans P10 (left edge) to P90 (right edge), so the
  // 0-100 percentile must be remapped onto that range. Salaries outside
  // P10-P90 clamp to the bar's edges.
  const markerPosition = percentile != null
    ? Math.min(99, Math.max(1, ((percentile - 10) / 80) * 100))
    : null;

  const verdict = useMemo(() => {
    if (percentile == null) return null;
    if (percentile < 35) return { label: 'Likely underpaid', tone: 'underpaid', icon: TrendingDown, color: 'text-red-600 dark:text-red-400', bg: 'bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-900' };
    if (percentile <= 65) return { label: 'Around market rate', tone: 'fair', icon: CheckCircle, color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:border-blue-900' };
    return { label: 'Paid above market', tone: 'overpaid', icon: TrendingUp, color: 'text-green-600 dark:text-green-400', bg: 'bg-green-50 border-green-200 dark:bg-green-950/30 dark:border-green-900' };
  }, [percentile]);

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Tool */}
      <Card className="text-left">
        <CardHeader className={variant === 'compact' ? 'pb-3' : undefined}>
          <CardTitle>Check your salary</CardTitle>
          {variant === 'full' && (
            <CardDescription>Enter your job, location, and current salary.</CardDescription>
          )}
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Occupation */}
          <div className="relative">
            <Label className="mb-1 block">Your job title</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder="e.g. registered nurse"
                value={selected ? toTitleCase(selected.occ_title) : query}
                onChange={(e) => {
                  setSelected(null);
                  setSubmitted(false);
                  setQuery(e.target.value);
                }}
              />
            </div>
            {!selected && debounced.length >= 2 && suggestions && suggestions.length > 0 && (
              <div className="absolute z-50 left-0 right-0 mt-1 bg-popover border rounded-md shadow-lg">
                <Command>
                  <CommandList className="max-h-64">
                    <CommandEmpty>No occupations found.</CommandEmpty>
                    <CommandGroup>
                      {suggestions.map((occ) => (
                        <CommandItem
                          key={occ.soc_code}
                          onSelect={() => {
                            setSelected(occ);
                            setQuery('');
                            onOccupationSelect?.({ code: occ.soc_code, title: occ.occ_title });
                          }}
                          className="cursor-pointer"
                        >
                          {toTitleCase(occ.occ_title)}
                          <span className="ml-auto text-xs text-muted-foreground">{occ.soc_code}</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </div>
            )}
          </div>

          {/* Location */}
          <div>
            <Label className="mb-1 block">Location</Label>
            <Select value={locationSlug} onValueChange={(v) => { setLocationSlug(v); setSubmitted(false); }}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="united-states">United States (national)</SelectItem>
                {STATE_MAPPINGS.map((s) => (
                  <SelectItem key={s.slug} value={s.slug}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Salary */}
          <div>
            <Label className="mb-1 block">Your current annual salary</Label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-9"
                inputMode="numeric"
                placeholder="e.g. 72000"
                value={salaryInput}
                onChange={(e) => { setSalaryInput(e.target.value); setSubmitted(false); }}
              />
            </div>
          </div>

          <Button
            className="w-full"
            size="lg"
            disabled={!canSubmit}
            onClick={() => setSubmitted(true)}
          >
            See if I'm underpaid
          </Button>
        </CardContent>
      </Card>

      {/* Result */}
      {submitted && selected && (
        <Card className={`text-left ${verdict ? verdict.bg : ''}`}>
          <CardContent>
            {distLoading ? (
              <div className="space-y-3">
                <div className="h-6 w-48 bg-muted rounded animate-pulse" />
                <div className="h-20 w-full bg-muted/50 rounded animate-pulse" />
              </div>
            ) : !distribution || !distribution.annual_median_wage ? (
              <p className="text-muted-foreground">
                We couldn't find wage data for that job in {locationName}. Try the national level or a different job title.
              </p>
            ) : percentile != null && verdict ? (
              <div className="space-y-5">
                <div className="flex items-center gap-3">
                  <verdict.icon className={`h-8 w-8 ${verdict.color}`} />
                  <div>
                    <div className={`text-2xl font-bold ${verdict.color}`}>{verdict.label}</div>
                    <div className="text-sm text-muted-foreground">
                      You're around the <strong>{percentile}th percentile</strong> for {toTitleCase(selected.occ_title)} in {locationName}.
                    </div>
                  </div>
                </div>

                {/* Distribution bar (spans P10 to P90) */}
                <div>
                  <div className="relative h-3 rounded-full bg-gradient-to-r from-red-300 via-blue-300 to-green-300 dark:from-red-500/60 dark:via-blue-500/60 dark:to-green-500/60">
                    <div
                      className="absolute -top-1 h-5 w-1.5 bg-foreground rounded"
                      style={{ left: `${markerPosition}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-muted-foreground mt-1">
                    <span>{formatCurrency(distribution.annual_10th_percentile_wage || 0)}</span>
                    <span>Median {formatCurrency(median)}</span>
                    <span>{formatCurrency(distribution.annual_90th_percentile_wage || 0)}</span>
                  </div>
                  {percentile < 10 && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Your salary is below the 10th percentile ({formatCurrency(distribution.annual_10th_percentile_wage || 0)}), the start of this range.
                    </p>
                  )}
                  {percentile > 90 && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Your salary is above the 90th percentile ({formatCurrency(distribution.annual_90th_percentile_wage || 0)}), the end of this range.
                    </p>
                  )}
                </div>

                <div className="text-sm">
                  {salaryNumber < median ? (
                    <p>
                      The median is <strong>{formatCurrency(median)}</strong> - about{' '}
                      <strong>{formatCurrency(median - salaryNumber)}</strong> more than you earn now. That's a concrete
                      number to bring to a raise conversation.
                    </p>
                  ) : (
                    <p>
                      You're earning <strong>{formatCurrency(salaryNumber - median)}</strong> above the median of{' '}
                      <strong>{formatCurrency(median)}</strong>. Nicely done.
                    </p>
                  )}
                </div>

                {/* Absolute income standing - second insight after the
                    occupation-relative percentile verdict above. */}
                <FinancialStatusInsight
                  annualIncome={salaryNumber}
                  contextLabel="Your salary"
                />

                <p className="text-sm text-muted-foreground">
                  The percentile above compares you to others in your occupation. To see how{' '}
                  {formatCurrency(salaryNumber)} ranks against all US earners instead, check the{' '}
                  <Link to="/income-percentile/" className="text-primary hover:underline underline-offset-2">
                    income percentile calculator
                  </Link>
                  .
                </p>

                {/* AI Outlook - third insight: how the AI economy affects this
                    occupation's pay trajectory. */}
                {(() => {
                  const aiEconomyMatch = getAiEconomyOccupation(selected.soc_code, selected.occ_title);
                  const aiRisk = calculateAIRiskScore(selected.soc_code, selected.occ_title);
                  const isHighRisk = aiRisk.riskScore >= 50;

                  if (aiEconomyMatch) {
                    return (
                      <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 space-y-2">
                        <div className="flex items-center gap-2">
                          <Zap className="h-5 w-5 text-primary" />
                          <span className="font-semibold">AI Outlook: this is an AI-economy occupation</span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          BLS projects <strong>+{aiEconomyMatch.projectedGrowthPct}%</strong> employment growth
                          for {toTitleCase(selected.occ_title)} through 2034
                          {salaryNumber < median
                            ? `, and workers with AI skills command a ~${AI_ECONOMY_STATS.aiWagePremiumPct}% wage premium. Growing demand plus AI upskilling is your strongest raise lever.`
                            : `, and workers with AI skills command a ~${AI_ECONOMY_STATS.aiWagePremiumPct}% wage premium on top.`}
                        </p>
                        <Link
                          to="/ai-economy-jobs"
                          className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline underline-offset-2"
                        >
                          See all AI-economy jobs &amp; salaries
                          <ArrowRight className="h-3.5 w-3.5" />
                        </Link>
                      </div>
                    );
                  }

                  if (isHighRisk) {
                    return (
                      <div className="rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30 p-4 space-y-2">
                        <div className="flex items-center gap-2">
                          <ShieldAlert className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                          <span className="font-semibold">AI Outlook: elevated automation exposure</span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {toTitleCase(selected.occ_title)} scores {aiRisk.riskScore}/100 on our AI-exposure
                          heuristic ({aiRisk.riskLevel.toLowerCase()} risk). Worth knowing before your next
                          negotiation - and worth a look at the careers AI is creating instead.
                        </p>
                        <div className="flex flex-wrap gap-x-4 gap-y-1">
                          <Link
                            to="/ai-career-analysis"
                            className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline underline-offset-2"
                          >
                            Get my full AI risk analysis
                            <ArrowRight className="h-3.5 w-3.5" />
                          </Link>
                          <Link
                            to="/ai-economy-jobs"
                            className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline underline-offset-2"
                          >
                            AI-resilient alternatives
                            <ArrowRight className="h-3.5 w-3.5" />
                          </Link>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <p className="text-sm text-muted-foreground">
                      Curious how AI affects {toTitleCase(selected.occ_title)} pay?{' '}
                      <Link to="/ai-career-analysis" className="text-primary hover:underline underline-offset-2">
                        Check your AI risk score
                      </Link>{' '}
                      or explore{' '}
                      <Link to="/ai-economy-jobs" className="text-primary hover:underline underline-offset-2">
                        the careers the AI economy is creating
                      </Link>
                      .
                    </p>
                  );
                })()}

                {/* Free report + high-intent CTAs */}
                <div className="rounded-lg border bg-background p-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <FileText className="h-5 w-5 text-primary mt-0.5" />
                    <div className="flex-1">
                      <div className="font-semibold">Get your free negotiation-ready report</div>
                      <p className="text-sm text-muted-foreground">
                        A polished PDF with your percentile, the full wage distribution, growth outlook and AI-risk -
                        perfect to attach to a raise request. Free, delivered to your inbox.
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button onClick={() => setShowPdfModal(true)} className="flex-1">
                      Get my free report
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                    <AffiliateCta
                      provider="ziprecruiter"
                      occupation={toTitleCase(selected.occ_title)}
                      location={locationName === 'United States' ? undefined : locationName}
                      type="job-search"
                      className="flex-1"
                    />
                  </div>
                  {salaryNumber < median && (
                    <AffiliateCta
                      provider="coursera"
                      occupation={toTitleCase(selected.occ_title)}
                      type="training"
                      className="w-full"
                    />
                  )}
                </div>

                <Badge variant="outline" className="text-xs">Source: U.S. Bureau of Labor Statistics (OEWS)</Badge>

                {variant === 'compact' && (
                  <a
                    href="#geographic-analysis"
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById('geographic-analysis')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="flex items-center gap-1.5 text-sm font-medium text-wage-accent hover:underline underline-offset-2 pt-1"
                  >
                    <MapPin className="h-4 w-4" />
                    See how {toTitleCase(selected.occ_title)} salaries vary by state&nbsp;&darr;
                  </a>
                )}
              </div>
            ) : null}
          </CardContent>
        </Card>
      )}

      {selected && (
        <FreeReportModal
          isOpen={showPdfModal}
          onClose={() => setShowPdfModal(false)}
          occupation={toTitleCase(selected.occ_title)}
          area={locationName}
          soc={selected.soc_code}
          source="underpaid_checker"
        />
      )}
    </div>
  );
};

export default UnderpaidChecker;
